import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChatWindowProps } from './types';

interface ExtendedChatWindowProps extends ChatWindowProps {
  isMinimized?: boolean;
}

const ChatWindow: React.FC<ExtendedChatWindowProps> = ({
  isOpen,
  onClose,
  theme: _theme,
  className = '',
  isMinimized = false,
  children
}) => {
  const windowRef = useRef<HTMLDivElement>(null);

  // ─── Drag & resize state (persisted to localStorage) ──────
  const [position, setPosition] = useState<{ x: number; y: number } | null>(() => {
    try { const s = localStorage.getItem('ai-chat-pos'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [size, setSize] = useState<{ w: number; h: number } | null>(() => {
    try { const s = localStorage.getItem('ai-chat-size'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const isDragging = useRef(false);
  const isResizing = useRef<string | null>(null);

  // Persist to localStorage on change (debounced via ref to avoid writes on every pixel)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (position) localStorage.setItem('ai-chat-pos', JSON.stringify(position));
      else localStorage.removeItem('ai-chat-pos');
      if (size) localStorage.setItem('ai-chat-size', JSON.stringify(size));
      else localStorage.removeItem('ai-chat-size');
    }, 300);
  }, [position, size]);

  const handleHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, nav, input, textarea, a')) return;
    const el = windowRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = rect.left;
    const origY = rect.top;

    isDragging.current = true;
    el.style.transition = 'none';

    const onMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 200, origX + ev.clientX - startX)),
        y: Math.max(0, Math.min(window.innerHeight - 100, origY + ev.clientY - startY)),
      });
    };

    const onUp = () => {
      isDragging.current = false;
      if (el) el.style.transition = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    e.preventDefault();
  }, []);

  const handleResizeMouseDown = useCallback((edge: string) => (e: React.MouseEvent) => {
    const el = windowRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const origW = rect.width;
    const origH = rect.height;
    const origX = rect.left;
    const origY = rect.top;

    isResizing.current = edge;
    el.style.transition = 'none';

    const MIN_W = 380;
    const MIN_H = 400;

    const onMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;

      let newW = origW, newH = origH, newX = origX, newY = origY;

      if (edge.includes('e')) newW = Math.max(MIN_W, origW + dx);
      if (edge.includes('s')) newH = Math.max(MIN_H, origH + dy);
      if (edge.includes('w')) { newW = Math.max(MIN_W, origW - dx); newX = origX + origW - newW; }
      if (edge.includes('n')) { newH = Math.max(MIN_H, origH - dy); newY = origY + origH - newH; }

      setSize({ w: newW, h: newH });
      setPosition({ x: newX, y: newY });
    };

    const onUp = () => {
      isResizing.current = null;
      if (el) el.style.transition = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Don't reset on close — position/size persist across open/close and page reloads
  // User can double-click header to reset to default position

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus management
  useEffect(() => {
    if (isOpen && windowRef.current) {
      windowRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Minimized compact chatbot window — EXACT original
  if (isMinimized) {
    return (
      <>
        <style>{`
          .glass-elevation {
            box-shadow: 0 0 1px rgba(0,0,0,0.1), 0 4px 6px -1px rgba(0,0,0,0.05), 0 20px 25px -5px rgba(0,0,0,0.1);
          }
          .fade-up {
            animation: fadeUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .loading-bar {
            width: 100%;
            height: 2px;
            background: linear-gradient(90deg, transparent, #3b82f6, transparent);
            background-size: 200% 100%;
            animation: move-bg 2s linear infinite;
          }
          @keyframes move-bg {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          .hover-scroll {
            scrollbar-width: thin;
            scrollbar-color: transparent transparent;
            overflow-y: auto;
          }
          .hover-scroll:hover {
            scrollbar-color: rgba(203, 213, 225, 0.6) transparent;
          }
          .hover-scroll::-webkit-scrollbar { width: 6px; }
          .hover-scroll::-webkit-scrollbar-track { background: transparent; }
          .hover-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 3px; }
          .hover-scroll:hover::-webkit-scrollbar-thumb { background: rgba(203, 213, 225, 0.6); }
          .hover-scroll::-webkit-scrollbar-thumb:hover { background: rgba(148, 163, 184, 0.8); }
        `}</style>

        <div
          ref={windowRef}
          className={`fixed bottom-28 right-10 w-[380px] h-[520px] max-w-[92vw] max-h-[70vh] ${_theme.isDark ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-700' : 'bg-gradient-to-br from-white via-slate-50 to-blue-50 border-slate-200'} rounded-2xl shadow-2xl backdrop-blur-xl overflow-hidden z-50 flex flex-col fade-up border ${className}`}
          role="dialog"
          aria-modal="true"
          aria-label="EIS-Tech AI Assistant"
          tabIndex={-1}
        >
          {children}
        </div>
      </>
    );
  }

  // Full expanded window — with draggable header + resizable edges
  const posStyle: React.CSSProperties = {
    ...(position ? { left: position.x, top: position.y, bottom: 'auto', right: 'auto' } : {}),
    ...(size ? { width: size.w, height: size.h } : {}),
  };

  const childArray = React.Children.toArray(children);

  // Edge handles — invisible 6px strips on each edge/corner
  const edges = [
    { id: 'n',  style: { top: 0, left: 8, right: 8, height: 6, cursor: 'ns-resize' } },
    { id: 's',  style: { bottom: 0, left: 8, right: 8, height: 6, cursor: 'ns-resize' } },
    { id: 'w',  style: { top: 8, bottom: 8, left: 0, width: 6, cursor: 'ew-resize' } },
    { id: 'e',  style: { top: 8, bottom: 8, right: 0, width: 6, cursor: 'ew-resize' } },
    { id: 'nw', style: { top: 0, left: 0, width: 12, height: 12, cursor: 'nwse-resize' } },
    { id: 'ne', style: { top: 0, right: 0, width: 12, height: 12, cursor: 'nesw-resize' } },
    { id: 'sw', style: { bottom: 0, left: 0, width: 12, height: 12, cursor: 'nesw-resize' } },
    { id: 'se', style: { bottom: 0, right: 0, width: 12, height: 12, cursor: 'nwse-resize' } },
  ];

  return (
    <>
      <style>{`
        .glass-elevation {
          box-shadow: 0 0 1px rgba(0,0,0,0.1), 0 4px 6px -1px rgba(0,0,0,0.05), 0 20px 25px -5px rgba(0,0,0,0.1);
        }
        .fade-up {
          animation: fadeUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .loading-bar {
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, transparent, #3b82f6, transparent);
          background-size: 200% 100%;
          animation: move-bg 2s linear infinite;
        }
        @keyframes move-bg {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .hover-scroll {
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
          overflow-y: auto;
        }
        .hover-scroll:hover {
          scrollbar-color: rgba(203, 213, 225, 0.6) transparent;
        }
        .hover-scroll::-webkit-scrollbar { width: 6px; }
        .hover-scroll::-webkit-scrollbar-track { background: transparent; }
        .hover-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 3px; }
        .hover-scroll:hover::-webkit-scrollbar-thumb { background: rgba(203, 213, 225, 0.6); }
        .hover-scroll::-webkit-scrollbar-thumb:hover { background: rgba(148, 163, 184, 0.8); }

        /* Container query responsive layout */
        .ai-chat-container { container-type: inline-size; }
        .ai-chat-container aside { display: none; }
        @container (min-width: 800px) {
          .ai-chat-container aside { display: flex; }
        }
        .ai-chat-container nav.ai-tab-nav { display: none; }
        @container (min-width: 600px) {
          .ai-chat-container nav.ai-tab-nav { display: flex; }
        }
        .ai-chat-container .ai-header-title { display: none; }
        @container (min-width: 500px) {
          .ai-chat-container .ai-header-title { display: block; }
        }
        .ai-chat-container .ai-header-status { display: none; }
        @container (min-width: 700px) {
          .ai-chat-container .ai-header-status { display: block; }
        }
        .ai-chat-container .ai-header-divider { display: none; }
        @container (min-width: 700px) {
          .ai-chat-container .ai-header-divider { display: block; }
        }
      `}</style>

      <div
        ref={windowRef}
        className={`ai-chat-container fixed ${position ? '' : 'bottom-32 right-10'} ${size ? '' : 'w-[520px] sm:w-[600px] md:w-[680px] lg:w-[920px] xl:w-[1120px] h-[760px] xl:h-[820px] max-w-[95vw] max-h-[85vh]'} ${_theme.isDark ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-700' : 'bg-gradient-to-br from-white via-slate-50 to-blue-50 border-slate-200'} rounded-2xl shadow-2xl backdrop-blur-xl overflow-hidden z-50 flex flex-col fade-up ${className}`}
        style={posStyle}
        role="dialog"
        aria-modal="true"
        aria-label="EIS-Tech AI Assistant"
        tabIndex={-1}
      >
        {/* Resize handles on all edges */}
        {edges.map(({ id, style }) => (
          <div
            key={id}
            onMouseDown={handleResizeMouseDown(id)}
            style={{ position: 'absolute', zIndex: 60, ...style } as React.CSSProperties}
          />
        ))}
        {/* Header with drag handle — double-click to reset position/size */}
        <div
          onMouseDown={handleHeaderMouseDown}
          onDoubleClick={() => { setPosition(null); setSize(null); }}
          style={{ cursor: 'grab' }}
        >
          {childArray[0]}
        </div>
        {/* Rest of content */}
        {childArray.slice(1)}
      </div>
    </>
  );
};

export default ChatWindow;
